import { useState, useEffect, useCallback } from "react";
import { StudySessionCalendar } from "../src/types/reviews";
import { reviewsUpdateEvent } from "./reviewsUpdateEvent";
import { api } from "../src/utils/axiosConfig";

// Interceptor para agregar token automáticamente
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useCalendarSessions = () => {
  const [sessions, setSessions] = useState<StudySessionCalendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchCalendarSessions = useCallback(async (days: number = 30) => {
    try {
      setLoading(true);

      const allSessions: StudySessionCalendar[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const { data } = await api.get(`/reviews/upcoming`, {
          params: { days, page, limit: 100 },
        });

        if (!data) {
          break;
        }

        if (data.upcomingReviews && Array.isArray(data.upcomingReviews)) {
          data.upcomingReviews.forEach((session: any) => {
            allSessions.push({
              id: session.id,
              dueDate: session.dueDate,
              card: session.card,
              intervalDays: session.intervalDays,
            });
          });
        } else if (
          data.upcomingReviews &&
          typeof data.upcomingReviews === "object"
        ) {
          Object.values(data.upcomingReviews).forEach((dateGroup: any) => {
            if (Array.isArray(dateGroup)) {
              dateGroup.forEach((session: any) => {
                allSessions.push({
                  id: session.id,
                  dueDate: session.dueDate,
                  card: session.card,
                  intervalDays: session.intervalDays,
                });
              });
            }
          });
        }

        if (data.pagination && page < data.pagination.totalPages) {
          page++;
        } else {
          hasMore = false;
        }
      }

      setSessions(allSessions);
      return allSessions;
    } catch (err) {
      setSessions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSessions = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchCalendarSessions();
  }, [fetchCalendarSessions, refreshTrigger]);

  // Para la actualización del widget del calendario sin refresh
  useEffect(() => {
    const handleReviewsUpdate = () => {
      refreshSessions();
    };

    // ESCUCHADOR del evento
    reviewsUpdateEvent.addListener(handleReviewsUpdate);

    return () => {
      reviewsUpdateEvent.removeListener(handleReviewsUpdate);
    };
  }, [refreshSessions]);

  return {
    sessions,
    loading,
    refreshSessions,
  };
};
